import { firestoreService } from './firebase'

/** Catalog of assignable permissions (UI + role docs) */
export const PERMISSION_CATALOG = [
  { id: 'access_pos', label: 'Access POS', group: 'Sales' },
  { id: 'perform_cash_up', label: 'Perform cash-up', group: 'Sales' },
  { id: 'apply_discounts', label: 'Apply discounts', group: 'Sales' },
  { id: 'upsell_services', label: 'Upsell services', group: 'Sales' },
  { id: 'issue_refunds', label: 'Issue refunds', group: 'Sales' },
  { id: 'scan_qr', label: 'Scan QR / check-in', group: 'Operations' },
  { id: 'create_wash', label: 'Create washes', group: 'Operations' },
  { id: 'update_wash_status', label: 'Update wash status', group: 'Operations' },
  { id: 'view_customers', label: 'View / manage customers', group: 'Business' },
  { id: 'manage_products', label: 'Manage products', group: 'Business' },
  { id: 'manage_inventory', label: 'Manage inventory', group: 'Business' },
  { id: 'manage_expenses', label: 'Manage expenses', group: 'Business' },
  { id: 'manage_subscriptions', label: 'Manage subscriptions', group: 'Business' },
  { id: 'manage_wash_catalog', label: 'Manage wash catalog', group: 'Business' },
  { id: 'manage_dealers', label: 'Manage dealers', group: 'Business' },
  { id: 'view_reports', label: 'View reports', group: 'Management' },
  { id: 'view_all_reports', label: 'View all-branch reports', group: 'Management' },
  { id: 'manage_staff', label: 'Manage staff / users', group: 'Management' },
  { id: 'manage_roles', label: 'Manage roles', group: 'Management' },
  { id: 'manage_branches', label: 'Manage branches', group: 'System' },
  { id: 'system_settings', label: 'System settings', group: 'System' },
  { id: 'full_access', label: 'Full access (all permissions)', group: 'System' },
]

const SYSTEM_ROLES = [
  {
    key: 'customer',
    name: 'Customer',
    description: 'Customer app access only',
    permissions: [],
    hierarchyLevel: 1,
    isSystem: true,
  },
  {
    key: 'staff',
    name: 'Staff',
    description: 'Floor staff — check-in and wash queue',
    permissions: ['scan_qr', 'create_wash', 'update_wash_status'],
    hierarchyLevel: 2,
    isSystem: true,
  },
  {
    key: 'manager',
    name: 'Manager',
    description: 'Branch manager — POS, inventory, reports',
    permissions: [
      'scan_qr',
      'create_wash',
      'update_wash_status',
      'access_pos',
      'perform_cash_up',
      'apply_discounts',
      'view_reports',
      'view_customers',
      'manage_products',
      'manage_inventory',
      'manage_expenses',
      'manage_subscriptions',
      'manage_wash_catalog',
      'manage_dealers',
      'upsell_services',
      'issue_refunds',
    ],
    hierarchyLevel: 3,
    isSystem: true,
  },
  {
    key: 'owner',
    name: 'Owner',
    description: 'Full business access',
    permissions: [
      'full_access',
      'manage_roles',
      'manage_branches',
      'view_all_reports',
      'manage_staff',
      'system_settings',
    ],
    hierarchyLevel: 4,
    isSystem: true,
  },
]

function slugifyRoleKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '')
}

/** Normalize permission ids from UI / legacy camelCase / labels */
export function normalizePermission(permission) {
  const raw = String(permission || '').trim()
  if (!raw) return ''

  // "Access POS" / accessPOS / access-pos → access_pos
  const snake = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')

  const aliases = {
    accesspos: 'access_pos',
    access_pos: 'access_pos',
    performcashup: 'perform_cash_up',
    perform_cash_up: 'perform_cash_up',
    cashup: 'perform_cash_up',
    cash_up: 'perform_cash_up',
    cashups: 'perform_cash_up',
    cash_ups: 'perform_cash_up',
    applydiscounts: 'apply_discounts',
    upsellservices: 'upsell_services',
    issuerefunds: 'issue_refunds',
    scanqr: 'scan_qr',
    scan_qr_check_in: 'scan_qr',
    createwashes: 'create_wash',
    create_washes: 'create_wash',
    updatewashstatus: 'update_wash_status',
    managewashqueue: 'update_wash_status',
    viewcustomers: 'view_customers',
    view_customers: 'view_customers',
    manageproducts: 'manage_products',
    manage_products: 'manage_products',
    manageinventory: 'manage_inventory',
    manageexpenses: 'manage_expenses',
    manage_expenses: 'manage_expenses',
    managesubscriptions: 'manage_subscriptions',
    manage_subscriptions: 'manage_subscriptions',
    managewashcatalog: 'manage_wash_catalog',
    manage_wash_catalog: 'manage_wash_catalog',
    managedealers: 'manage_dealers',
    manage_dealers: 'manage_dealers',
    viewreports: 'view_reports',
    viewallreports: 'view_all_reports',
    view_all_reports: 'view_all_reports',
    managestaff: 'manage_staff',
    manageroles: 'manage_roles',
    managebranches: 'manage_branches',
    systemsettings: 'system_settings',
    fullaccess: 'full_access',
  }

  const compact = snake.replace(/_/g, '')
  return aliases[snake] || aliases[compact] || snake
}

function normalizePermissionList(list) {
  return [...new Set((list || []).map(normalizePermission).filter(Boolean))]
}

export class RoleService {
  // Kept for backwards compatibility with permissions.js helpers
  static defaultPermissions = {
    customer: SYSTEM_ROLES[0].permissions,
    staff: SYSTEM_ROLES[1].permissions,
    manager: SYSTEM_ROLES[2].permissions,
    owner: SYSTEM_ROLES[3].permissions,
  }

  async ensureDefaultRoles() {
    const existing = await this.getRoles()
    const byKey = new Map(
      existing.map((r) => [String(r.key || r.id || '').toLowerCase(), r])
    )

    for (const seed of SYSTEM_ROLES) {
      const found = byKey.get(seed.key)
      if (!found) {
        await firestoreService.create('roles', {
          ...seed,
          createdAt: new Date(),
          updatedAt: new Date(),
        })
      }
    }

    return this.getRoles()
  }

  async createRole(roleData) {
    try {
      const key = slugifyRoleKey(roleData.key || roleData.name)
      if (!key) throw new Error('Role name is required')
      if (['owner', 'customer', 'staff', 'manager'].includes(key) && roleData.isSystem !== true) {
        // Allow creating only if not colliding with system keys unless seeding
        const existing = await this.getRoles()
        if (existing.some((r) => String(r.key || r.id).toLowerCase() === key)) {
          throw new Error('A role with this key already exists')
        }
      }

      const payload = {
        key,
        name: String(roleData.name || key).trim(),
        description: String(roleData.description || ''),
        permissions: normalizePermissionList(roleData.permissions),
        hierarchyLevel: Number(roleData.hierarchyLevel || 2),
        isSystem: Boolean(roleData.isSystem),
        createdAt: new Date(),
        updatedAt: new Date(),
      }

      const id = await firestoreService.create('roles', payload)
      return { id, ...payload }
    } catch (error) {
      console.error('Error creating role:', error)
      throw error instanceof Error ? error : new Error('Failed to create role')
    }
  }

  async getRoles() {
    try {
      const list = await firestoreService.getAll('roles')
      return (list || [])
        .map((r) => ({
          ...r,
          key: r.key || r.id,
          permissions: Array.isArray(r.permissions)
            ? normalizePermissionList(r.permissions)
            : [],
        }))
        .sort((a, b) => Number(b.hierarchyLevel || 0) - Number(a.hierarchyLevel || 0) || String(a.name).localeCompare(String(b.name)))
    } catch (error) {
      // Customers/staff may lack roles read until rules allow it — fall back quietly.
      if (error?.code === 'permission-denied') {
        console.warn('Roles collection not readable for this user; using defaults')
      } else {
        console.error('Error fetching roles:', error)
      }
      return []
    }
  }

  async getRoleByKey(roleKey) {
    const key = String(roleKey || '').toLowerCase()
    if (!key) return null
    const roles = await this.getRoles()
    return (
      roles.find((r) => String(r.key || '').toLowerCase() === key) ||
      roles.find((r) => String(r.id || '').toLowerCase() === key) ||
      null
    )
  }

  async updateRole(roleId, updateData) {
    try {
      const patch = { ...updateData, updatedAt: new Date() }
      if (patch.permissions) {
        patch.permissions = normalizePermissionList(patch.permissions)
      }
      await firestoreService.update('roles', roleId, patch)

      // Push updated permissions to every user with this role
      if (patch.permissions) {
        const roles = await this.getRoles()
        const role = roles.find((r) => r.id === roleId)
        const roleKey = role?.key || role?.id
        if (roleKey) {
          await this.syncRolePermissionsToUsers(roleKey, patch.permissions)
        }
      }

      return true
    } catch (error) {
      console.error('Error updating role:', error)
      throw new Error('Failed to update role')
    }
  }

  /** Replace permissions on all users assigned to this role key */
  async syncRolePermissionsToUsers(roleKey, permissions) {
    const key = String(roleKey || '').toLowerCase()
    if (!key) return 0
    const normalized = normalizePermissionList(permissions)
    const users = await this.getUsersByRole(key)
    // Also catch users whose role casing differs
    const allUsers = users.length
      ? users
      : (await firestoreService.getAll('users')).filter(
          (u) => String(u.role || '').toLowerCase() === key
        )

    await Promise.all(
      (allUsers || []).map((u) =>
        firestoreService.update('users', u.id, {
          permissions: normalized,
          rolePermissionsSyncedAt: new Date(),
          updatedAt: new Date(),
        })
      )
    )
    return (allUsers || []).length
  }

  async deleteRole(roleId, { force = false } = {}) {
    try {
      const roles = await this.getRoles()
      const role = roles.find((r) => r.id === roleId)
      if (!role) throw new Error('Role not found')
      if (role.isSystem && !force) {
        throw new Error('System roles cannot be deleted')
      }
      const users = await this.getUsersByRole(role.key || role.id)
      if (users.length > 0) {
        throw new Error(`Cannot delete: ${users.length} user(s) still have this role`)
      }
      await firestoreService.delete('roles', roleId)
      return true
    } catch (error) {
      console.error('Error deleting role:', error)
      throw error instanceof Error ? error : new Error('Failed to delete role')
    }
  }

  async resolvePermissionsForRole(roleName, extraPermissions = []) {
    const roleDoc = await this.getRoleByKey(roleName)
    const base =
      roleDoc?.permissions ||
      RoleService.defaultPermissions[roleName] ||
      []
    return normalizePermissionList([...base, ...(extraPermissions || [])])
  }

  async assignRoleToUser(userId, roleName, permissions = [], assignedBy) {
    try {
      // If UI passed a full permission list, prefer that (already role-based);
      // still normalize. If empty, load from role doc.
      const fromRole = await this.resolvePermissionsForRole(roleName, [])
      const extras = normalizePermissionList(permissions)
      const allPermissions =
        extras.length > 0 ? normalizePermissionList([...fromRole, ...extras]) : fromRole

      await firestoreService.update('users', userId, {
        role: roleName,
        permissions: allPermissions,
        assignedBy,
        roleAssignedAt: new Date(),
      })

      return true
    } catch (error) {
      console.error('Error assigning role:', error)
      throw new Error('Failed to assign role')
    }
  }

  /**
   * Effective permissions: user.permissions ∪ role-doc permissions ∪ static defaults
   * (role doc is source of truth once loaded into userProfile.rolePermissions)
   */
  getEffectivePermissions(userProfile) {
    if (!userProfile) return []
    if (userProfile.role === 'owner') return ['full_access']

    const fromUser = normalizePermissionList(userProfile.permissions)
    const fromRoleDoc = normalizePermissionList(userProfile.rolePermissions)
    const fromDefaults = normalizePermissionList(
      RoleService.defaultPermissions[userProfile.role] || []
    )
    return [...new Set([...fromUser, ...fromRoleDoc, ...fromDefaults])]
  }

  hasPermission(userProfile, permission) {
    if (!userProfile) return false

    // Owners have full access
    if (userProfile.role === 'owner') return true

    const want = normalizePermission(permission)
    const permissions = this.getEffectivePermissions(userProfile)
    return permissions.includes(want) || permissions.includes('full_access')
  }

  canUserPerformAction(userProfile, action) {
    const actionPermissions = {
      access_pos: ['access_pos', 'full_access'],
      perform_cash_up: ['perform_cash_up', 'full_access'],
      apply_discounts: ['apply_discounts', 'full_access'],
      upsell_services: ['upsell_services', 'full_access'],
      issue_refunds: ['issue_refunds', 'full_access'],
      manage_roles: ['manage_roles', 'full_access'],
      view_reports: ['view_reports', 'view_all_reports', 'full_access'],
      manage_inventory: ['manage_inventory', 'full_access'],
      manage_products: ['manage_products', 'full_access'],
      manage_expenses: ['manage_expenses', 'full_access'],
      manage_subscriptions: ['manage_subscriptions', 'full_access'],
      manage_wash_catalog: ['manage_wash_catalog', 'full_access'],
      manage_dealers: ['manage_dealers', 'full_access'],
      view_customers: ['view_customers', 'full_access'],
    }

    const requiredPermissions = actionPermissions[action] || [action]
    return requiredPermissions.some((permission) => this.hasPermission(userProfile, permission))
  }

  async getUsersByRole(roleName) {
    try {
      const constraints = [['role', '==', roleName]]
      return await firestoreService.getAll('users', constraints)
    } catch (error) {
      console.error('Error fetching users by role:', error)
      return []
    }
  }

  async getRoleHierarchy() {
    const roles = await this.getRoles()
    const map = { owner: 4, manager: 3, staff: 2, customer: 1 }
    roles.forEach((r) => {
      const k = r.key || r.id
      if (k) map[k] = Number(r.hierarchyLevel || map[k] || 2)
    })
    return map
  }

  canUserManageRole(managerRole, targetRole) {
    const hierarchy = {
      owner: 4,
      manager: 3,
      staff: 2,
      customer: 1,
    }

    const managerLevel = hierarchy[managerRole] ?? 2
    const targetLevel = hierarchy[targetRole] ?? 2
    return managerLevel > targetLevel
  }
}

export const roleService = new RoleService()
